import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useQuery = () => new URLSearchParams(window.location.search);

export default function QrDisplay() {
  const q = useQuery();
  const orgId = q.get('org');
  const locationId = q.get('loc');

  const [info, setInfo] = useState(null);
  const [token, setToken] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [period, setPeriod] = useState(30);
  const [error, setError] = useState(null);

  const fetchToken = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/qr/display-token`, { params: { org_id: orgId, location_id: locationId } });
      setToken(res.data.token);
      setPeriod(res.data.period);
      setSecondsLeft(res.data.seconds_left);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo generar el código.');
    }
  }, [orgId, locationId]);

  useEffect(() => {
    if (!orgId || !locationId) {
      setError('Faltan parámetros (org / loc) en la URL.');
      return;
    }
    axios.get(`${API_URL}/qr/public-config`, { params: { org_id: orgId, location_id: locationId } })
      .then((res) => setInfo(res.data))
      .catch(() => {});
    fetchToken();
  }, [orgId, locationId, fetchToken]);

  // Countdown + auto refresh
  useEffect(() => {
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          fetchToken();
          return period;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [period, fetchToken]);

  const clockUrl = token
    ? `${window.location.origin}/qr-clock?org=${encodeURIComponent(orgId)}&loc=${encodeURIComponent(locationId)}&t=${encodeURIComponent(token)}`
    : '';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold">{info?.company_name || 'Fichaje QR'}</h1>
        {info?.location_name && <p className="text-slate-300 text-lg mt-1">{info.location_name}</p>}
      </div>

      {error ? (
        <div className="bg-red-500/20 border border-red-400 rounded-xl p-6 max-w-md text-center">
          <p className="text-red-200">{error}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl p-6 shadow-2xl">
            {token ? (
              <QRCodeSVG value={clockUrl} size={320} level="M" includeMargin />
            ) : (
              <div className="w-[320px] h-[320px] flex items-center justify-center text-slate-400">Cargando…</div>
            )}
          </div>
          <div className="mt-6 text-center">
            <p className="text-lg text-slate-200">Escanea con tu móvil para fichar</p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <div className="w-48 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-400 transition-all duration-1000 ease-linear"
                  style={{ width: `${(secondsLeft / period) * 100}%` }}
                />
              </div>
              <span className="text-slate-400 text-sm tabular-nums">{secondsLeft}s</span>
            </div>
            <p className="text-slate-500 text-sm mt-2">El código se renueva automáticamente por seguridad.</p>
          </div>
        </>
      )}
    </div>
  );
}
